import { createSelector } from 'reselect';
import {
  ArmyListUnitProfilV2View,
  ModelRuleView,
  TroopProfilView,
} from './view/ArmyListUnitProfilV2View';
import { AppState } from '@Redux/store';
import {
  ArmyListUnit,
  ArmyListUnitModelRuleTroop,
  ModelRule,
  ModelRuleChangeModelRule,
  ModelRuleUnitTroop,
  Nullable,
  Organisation,
  TypeLvl,
  TypeRule,
  UnitCarac,
} from '@Models';
import { UnitCaracView } from './view/UnitCaracView';
import { OrganisationView } from './view/OrganisationView';
import {
  getArmyListUnits,
  getModelRules,
  getOrganisations,
} from '../edit/selector/editSelector';

export const getArmyListUnitId = (_: AppState, props: any): string =>
  props.armyListUnitId;

export const ArmyListUnitProfilV2Presenter = createSelector(
  [getArmyListUnitId, getArmyListUnits, getModelRules, getOrganisations],
  (
    armyListUnitId,
    armyListUnits,
    modelRules,
    organisations,
  ): ArmyListUnitProfilV2View => {
    const armyListUnit = armyListUnits.find(
      (item) => item.id === armyListUnitId,
    );

    const unitModelRules = armyListUnit!.modelRuleUnitTroops
      .filter((item) => item.isEnabled)
      .filter((item) =>
        [TypeLvl.Global, TypeLvl.Army, TypeLvl.Defensive].includes(
          item.typeLvl,
        ),
      )
      .map((item) => armyListUnitModelRuleToView(item));

    const troops = armyListUnit!.troops.map((troop) => {
      return new TroopProfilView({
        name: troop.name,
        carac: troop.carac,
        modelRules: armyListUnit!.modelRuleUnitTroops
          .filter((item) => item.isEnabled)
          .filter((item) => item.typeLvl === TypeLvl.Offensive)
          .filter(
            (item) => item.troopId == null || item.troopId === troop.troopId,
          )
          .map((item) => armyListUnitModelRuleToView(item)),
      });
    });

    const changeProfils = troops
      .flatMap((item) => item.modelRules)
      .concat(unitModelRules)
      .map((item) => modelRules.find((rule) => rule.id === item.id))
      .filter((item): item is ModelRule => !!item)
      .flatMap((item) => item.changeProfils);

    return new ArmyListUnitProfilV2View({
      item: armyListUnit!,
      name: armyListUnit!.name,
      organisations: armyListUnit!.organisations.map(organisationToView),
      unitId: armyListUnit!.unit_id,
      carac: makeUnitCaracView(armyListUnit!.carac, changeProfils),
      modelRules: unitModelRules,
      troops: troops,
      mount: mountToView(armyListUnit!, organisations),
    });
  },
);

export const mountToView = (
  armyListUnit: ArmyListUnit,
  organisations: Organisation[],
): Nullable<ArmyListUnitProfilV2View> => {
  const mount = armyListUnit.mount;
  if (!mount) return null;

  const unitModelRules = mount.modelRuleUnitTroops
    .filter((item) => !item.troopId)
    .map((item) => modelRuleToView(item));

  const troops = mount.troops.map((troop) => {
    return new TroopProfilView({
      name: troop.name,
      carac: { ...troop.carac },
      modelRules: mount.modelRuleUnitTroops
        .filter((item) => item.typeLvl === TypeLvl.Offensive)
        .filter((item) => item.troopId == null || item.troopId === troop.id)
        .map((item) => modelRuleToView(item)),
    });
  });

  return new ArmyListUnitProfilV2View({
    item: armyListUnit,
    name: mount.name,
    organisations: organisations
      .filter((orgnisation) =>
        mount.allOrganisationIds.includes(orgnisation.id),
      )
      .map(organisationToView),
    unitId: mount.id,
    carac: makeUnitCaracView(mount.carac, []),
    modelRules: unitModelRules,
    troops: troops,
    mount: null,
  });
};

export const armyListUnitModelRuleToView = (
  item: ArmyListUnitModelRuleTroop,
  troopName: Nullable<string> = null,
): ModelRuleView => {
  return new ModelRuleView({
    id: item.modelRuleId,
    typeLvl: item.typeLvl,
    type: item.typeRule,
    name: troopName == null ? item.name : `${troopName}: ${item.name}`,
    description: item.description ?? '',
    checkable: false, //item.typeRule === TypeRule.Equipment,
    checked: item.isSelected,
    armyListUnitModelRuleId: item.id,
  });
};

export const modelRuleToView = (
  item: ModelRuleUnitTroop,
  troopName: Nullable<string> = null,
): ModelRuleView => {
  return new ModelRuleView({
    id: item.modelRuleId,
    typeLvl: item.typeLvl,
    type: item.typeRule,
    name: troopName == null ? item.name : `${troopName}: ${item.name}`,
    description: item.description ?? '',
    checkable: item.typeRule === TypeRule.Equipment,
    checked: false,
    armyListUnitModelRuleId: null,
  });
};

export const organisationToView = (item: Organisation): OrganisationView => {
  return new OrganisationView({
    name: item.name,
    logo: item.medium_logo,
  });
};

const cleanCarac = (str?: string | null): Nullable<string> => {
  return str?.replace(/\([A-Z, +\d]+\)+/, '')?.trim() ?? null;
};

export const makeUnitCaracView = (
  item: UnitCarac,
  changeProfils: ModelRuleChangeModelRule[],
): UnitCaracView => {
  return new UnitCaracView({
    type: item.type,
    adv: cleanCarac(item.ground_adv),
    mar: cleanCarac(item.ground_mar),
    dis: cleanCarac(item.dis),
    evoked: cleanCarac(item.evoked),
    hp: cleanCarac(item.hp),
    def: cleanCarac(item.def),
    res: cleanCarac(item.res),
    arm: cleanCarac(item.arm),
    aeg: cleanCarac(item.aeg),
    flyAdv: cleanCarac(item.fly_adv),
    flyMar: cleanCarac(item.fly_mar),
    base: item.unitBase.name,
    height: item.unitHeight.name,
    unitType: item.unitType?.name ?? '',
  });
};
